import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import DashboardShell from "@/components/DashboardShell";
import PrintLogoHeader from "@/components/PrintLogoHeader";
import api, { formatError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import PhoneMock from "@/components/PhoneMock";
import {
  Printer,
  Star,
  TruckIcon,
  Wrench,
  Camera,
  CircleCheck,
  DollarSign,
  MessageSquareText,
  Search,
  Bell,
  Tag,
  AlertTriangle,
  Sunrise,
  Sun,
  Moon,
  Flag,
  Car,
  Trophy,
  ClipboardCheck,
  Skull,
  Key,
  Disc,
  Square,
} from "lucide-react";

/**
 * PreOwned Posse — used vehicle recon pipeline.
 * A standalone brand under Ayers Enterprises. Distinct visual
 * identity: sheriff-badge palette (deep bronze + charcoal + cream)
 * separates it from the Decline Drive navy/gold.
 */

const BRAND_BRONZE = "#a06a1e";
const BRAND_BRONZE_DARK = "#7a4e0f";
const BRAND_CREAM = "#f6ecd8";

function money(n) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n || 0);
}

function ageStyle(days) {
  if (days == null) return "";
  if (days <= 3) return "bg-emerald-50 border-l-4 border-l-emerald-500 dark:bg-emerald-950/20";
  if (days <= 7) return "bg-yellow-50 border-l-4 border-l-yellow-500 dark:bg-yellow-950/20";
  return "bg-red-50 border-l-4 border-l-red-500 dark:bg-red-950/20";
}
function ageDot(days) {
  if (days == null) return "bg-neutral-400";
  if (days <= 3) return "bg-emerald-500";
  if (days <= 7) return "bg-yellow-500";
  return "bg-red-500";
}

// Clearance tiers now load from `GET /api/posse/clearance` so each dealer
// can plug in their own roster + permission model via `POST /api/posse/clearance`.
// Frontend keeps a safe default so the page renders while the fetch is inflight.
const DEFAULT_CLEARANCE_TIERS = [
  { id: "salesman", label: "Salesman", allowed: ["basic", "photos"] },
  { id: "manager",  label: "Manager (Recon/Body/Photos)", allowed: ["basic", "photos", "cost"] },
  { id: "used_car", label: "Used Car Manager", allowed: ["basic", "photos", "cost", "retail"] },
  { id: "gm",       label: "GM · Owner", allowed: ["basic", "photos", "cost", "retail", "gross"] },
];

const COLUMNS = [
  { key: "acquired", title: "Acquired", sub: "Trade or auction", Icon: TruckIcon, tint: "bg-neutral-800 text-white" },
  { key: "in_recon", title: "In Recon", sub: "Service · body · detail", Icon: Wrench, tint: "bg-orange-600 text-white" },
  { key: "merchandising", title: "Merchandising", sub: "Photos + pricing", Icon: Camera, tint: "bg-blue-600 text-white" },
  { key: "front_line", title: "Front Line", sub: "Live on the lot", Icon: Star, tint: "bg-[color:var(--posse-bronze)] text-white" },
  { key: "sold", title: "Sold", sub: "Cash collected", Icon: CircleCheck, tint: "bg-emerald-600 text-white" },
];

const RECON_ICON = {
  Mechanical: Wrench,
  "Body / Paint": Tag,
  Detail: Star,
  "Photos & Listing": Camera,
};

export default function PreownedPossePage() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [lookupVin, setLookupVin] = useState("");
  const [lookupResp, setLookupResp] = useState(null);
  const [digests, setDigests] = useState({ morning: null, midday: null, evening: null });
  const [compliance, setCompliance] = useState([]);
  const [reminders, setReminders] = useState({ recon: null, body: null, used_car: null });
  const [flags, setFlags] = useState([]);
  const [flagForm, setFlagForm] = useState({ vin: "", issue: "", reporter: "" });
  const [flagResult, setFlagResult] = useState(null);
  const [complianceText, setComplianceText] = useState(null);
  const [master, setMaster] = useState(null);
  const [sendingMaster, setSendingMaster] = useState(false);
  const [masterSentAt, setMasterSentAt] = useState(null);
  const [checkinToday, setCheckinToday] = useState(null);
  const [scoreboard, setScoreboard] = useState(null);
  const [photos, setPhotos] = useState(null);
  const [pastDue, setPastDue] = useState([]);
  const [photoStageOpen, setPhotoStageOpen] = useState(null);
  // Clearance-gated deal drawer — click a car to open the numbers.
  const [clearance, setClearance] = useState(
    typeof window !== "undefined"
      ? localStorage.getItem("posseClearance") || "gm"
      : "gm"
  );
  const [activeVehicle, setActiveVehicle] = useState(null);
  const [clearanceTiers, setClearanceTiers] = useState(DEFAULT_CLEARANCE_TIERS);
  const setClearanceLevel = (lvl) => {
    setClearance(lvl);
    try { localStorage.setItem("posseClearance", lvl); } catch (_) {}
  };
  const tierFor = (id) => {
    const list = clearanceTiers.length ? clearanceTiers : DEFAULT_CLEARANCE_TIERS;
    return list.find((t) => t.id === id) || list[list.length - 1];
  };
  const tierAllows = (tier, group) => {
    // Backend sends `allowed` as an array; local defaults are the same shape
    const set = Array.isArray(tier.allowed) ? new Set(tier.allowed) : tier.allowed;
    return set.has ? set.has(group) : new Set(tier.allowed || []).has(group);
  };

  const load = () => {
    setBusy(true);
    return api
      .get("/posse/inventory")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatError(e.response?.data?.detail)))
      .finally(() => setBusy(false));
  };
  useEffect(() => {
    load();
  }, []);

  // Load the three GM digest texts in parallel
  useEffect(() => {
    Promise.all([
      api.get("/posse/gm-digest?slot=morning").then((r) => r.data),
      api.get("/posse/gm-digest?slot=midday").then((r) => r.data),
      api.get("/posse/gm-digest?slot=evening").then((r) => r.data),
      api.get("/posse/compliance").then((r) => r.data),
      api.get("/posse/manager-reminder?manager=recon").then((r) => r.data),
      api.get("/posse/manager-reminder?manager=body").then((r) => r.data),
      api.get("/posse/manager-reminder?manager=used_car").then((r) => r.data),
      api.get("/posse/flags").then((r) => r.data),
      api.get("/posse/gm-compliance-text").then((r) => r.data),
      api.get("/posse/master-text").then((r) => r.data),
      api.get("/posse/checkin/today").then((r) => r.data),
      api.get("/posse/scoreboard").then((r) => r.data),
      api.get("/posse/photos").then((r) => r.data),
      api.get("/posse/clearance").then((r) => r.data).catch(() => ({ tiers: DEFAULT_CLEARANCE_TIERS })),
      api.get("/posse/past-due").then((r) => r.data).catch(() => ({ vehicles: [] })),
    ])
      .then(([m, n, e, comp, r1, r2, r3, fl, ct, mt, ci, sb, ph, cl, pd]) => {
        setDigests({ morning: m, midday: n, evening: e });
        setCompliance(comp.managers || []);
        setReminders({ recon: r1, body: r2, used_car: r3 });
        setFlags(fl.flags || []);
        setComplianceText(ct);
        setMaster(mt);
        if (mt?.last_sent?.created_at) setMasterSentAt(mt.last_sent.created_at);
        setCheckinToday(ci);
        setScoreboard(sb);
        setPhotos(ph);
        if (cl?.tiers?.length) setClearanceTiers(cl.tiers);
        setPastDue(pd?.vehicles || []);
      })
      .catch(() => {});
  }, []);

  const submitFlag = async () => {
    if (!flagForm.vin.trim() || !flagForm.issue.trim()) {
      toast.error("VIN + issue both required");
      return;
    }
    try {
      const { data } = await api.post("/posse/flag", {
        vin: flagForm.vin,
        issue: flagForm.issue,
        reporter: flagForm.reporter || "Salesman",
      });
      setFlagResult(data);
      toast.success("Flag sent to Recon Manager");
      const list = await api.get("/posse/flags");
      setFlags(list.data.flags || []);
      setFlagForm({ vin: "", issue: "", reporter: "" });
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  // Click-to-toggle a single status light. Optimistic UI: flip locally,
  // then persist. If the API call fails we roll back with a reload.
  // Passes vehicle context so the backend auto-texts the responsible
  // manager + GM (owner-notified audit trail).
  const toggleLight = async (vehicleId, key, nextGreen) => {
    let veh;
    setData((prev) => {
      if (!prev) return prev;
      const nextVehicles = prev.vehicles.map((v) => {
        if (v.id !== vehicleId) return v;
        veh = v;
        const nextLights = (v.status_lights || []).map((l) =>
          l.key === key ? { ...l, green: nextGreen, manual: true } : l
        );
        return {
          ...v,
          status_lights: nextLights,
          all_green: nextLights.every((l) => l.green),
        };
      });
      return { ...prev, vehicles: nextVehicles };
    });
    try {
      const { data: r } = await api.post(
        `/posse/vehicles/${vehicleId}/status-light`,
        {
          key,
          green: nextGreen,
          vehicle_stock_no: veh?.stock_no,
          vehicle_label: veh ? `${veh.year} ${veh.make_model}` : undefined,
          responsible_manager: veh?.responsible_manager,
        }
      );
      if (nextGreen) {
        const who = (r?.notified || []).map((n) => n.name).join(" + ") || "manager + GM";
        toast.success(`✓ ${key} marked done · ${who} notified`);
      }
    } catch (e) {
      toast.error("Couldn't save — reloading");
      await load();
    }
  };

  const sendMaster = async () => {
    setSendingMaster(true);
    try {
      const { data } = await api.post("/posse/master-text/send");
      toast.success(`Update sent to ${data.sent_count} managers`);
      setMasterSentAt(data.created_at);
      // refresh preview so the "last sent" line updates
      const { data: mt } = await api.get("/posse/master-text");
      setMaster(mt);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setSendingMaster(false);
    }
  };

  const columns = useMemo(() => {
    if (!data) return {};
    const g = { acquired: [], in_recon: [], merchandising: [], front_line: [], sold: [] };
    for (const v of data.vehicles) if (g[v.status]) g[v.status].push(v);
    return g;
  }, [data]);

  const totals = data?.totals || {};
  const latestFL = data?.latest_frontline;
  const dealer = data?.dealership_name || "Your Dealership";

  const doLookup = async () => {
    if (!lookupVin.trim()) return;
    try {
      const { data } = await api.get(`/posse/lookup?vin=${encodeURIComponent(lookupVin)}`);
      setLookupResp(data);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  return (
    <DashboardShell>
      {/* Scoped Posse brand color */}
      <style>{`.posse-scope { --posse-bronze: ${BRAND_BRONZE}; --posse-bronze-dark: ${BRAND_BRONZE_DARK}; --posse-cream: ${BRAND_CREAM}; }
        @keyframes posseConfetti {
          0% { opacity: 1; transform: translate(0,0) scale(1); }
          100% { opacity: 0; transform: translate(var(--tx, 20px), var(--ty, 30px)) scale(0.3); }
        }
      `}</style>

      <PrintLogoHeader docTitle="PreOwned Pipeline — Recon" />

      <div className="posse-scope">
        {/* Brand strip */}
        <div
          className="mb-6 rounded-xl overflow-hidden shadow-xl relative no-print"
          style={{
            background: `linear-gradient(135deg, ${BRAND_BRONZE_DARK} 0%, ${BRAND_BRONZE} 60%, #c98c3d 100%)`,
          }}
        >
          <div
            className="absolute inset-0 opacity-15"
            aria-hidden
            style={{
              backgroundImage:
                "repeating-linear-gradient(45deg, rgba(255,255,255,0.5) 0 1px, transparent 1px 12px)",
            }}
          />
          <div className="relative p-5 md:p-6 flex items-center gap-4 flex-wrap">
            <div
              className="w-14 h-14 rounded-full grid place-items-center shadow-xl ring-4 shrink-0"
              style={{ background: BRAND_CREAM, ringColor: BRAND_BRONZE_DARK }}
            >
              <Star
                className="w-8 h-8"
                strokeWidth={2.5}
                style={{ color: BRAND_BRONZE_DARK, fill: BRAND_BRONZE }}
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-mono-label text-[0.6rem] text-white/80 tracking-widest">
                A Standalone Product · Under Ayers Fixed Operations
              </div>
              <div className="font-display font-black text-4xl md:text-5xl tracking-tighter text-white leading-none mt-1">
                PreOwned Pipeline
              </div>
              <div className="font-display text-white/90 text-base md:text-lg mt-1">
                Every used vehicle. Every dollar. Front-line ready — spot on.
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => window.print()}
              data-testid="posse-print-btn"
              className="bg-white/95 hover:bg-white text-neutral-900 border-transparent"
            >
              <Printer className="w-4 h-4 mr-2" strokeWidth={1.75} />
              Print Inventory
            </Button>
            <Link to="/posse/past-due">
              <Button
                variant="outline"
                data-testid="posse-past-due-btn"
                className="bg-red-600 hover:bg-red-500 text-white border-transparent"
              >
                <AlertTriangle className="w-4 h-4 mr-2" strokeWidth={2} />
                Past Due
              </Button>
            </Link>
          </div>
        </div>

        {/* Top 4 Delayed Vehicles — clean white cards, dark text, red only for
            the days-count so the eye lands on the most urgent number. */}
        {pastDue.length > 0 && (
          <Card
            className="p-4 mb-4 border-l-4 border-l-red-600 bg-white dark:bg-neutral-900 no-print"
            data-testid="posse-top-delays"
          >
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <AlertTriangle className="w-5 h-5 text-red-700" strokeWidth={2.6} />
              <div className="font-display font-black text-base text-neutral-900 dark:text-white">
                Top {Math.min(4, pastDue.length)} Delayed Vehicles
              </div>
              <div className="font-mono-label text-[0.62rem] font-bold tracking-widest text-neutral-700 dark:text-neutral-300">
                &gt; 7 DAYS IN RECON · {pastDue.length} TOTAL
              </div>
              <Link to="/posse/past-due" className="ml-auto">
                <Button
                  size="sm"
                  className="h-8 bg-red-600 hover:bg-red-500 text-white font-semibold"
                  data-testid="posse-top-delays-see-all"
                >
                  See all {pastDue.length} delays →
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {pastDue.slice(0, 4).map((v) => (
                <Link
                  key={v.id}
                  to="/posse/past-due"
                  className="rounded-lg border-2 border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-3 hover:border-red-500 hover:shadow-md transition-all"
                  data-testid={`posse-top-delay-${v.id}`}
                >
                  <div className="flex items-baseline justify-between mb-1">
                    <div className="font-bold text-sm truncate text-neutral-900 dark:text-white">
                      {v.year} {v.make_model}
                    </div>
                    <div className="font-display font-black text-lg tabular-nums text-red-600 shrink-0 ml-2">
                      {v.days_in_recon}d
                    </div>
                  </div>
                  <div className="text-[0.7rem] text-neutral-700 dark:text-neutral-300 mb-2 font-mono font-semibold">
                    Stock {v.stock_no} · {v.responsible_manager}
                  </div>
                  <div className="text-[0.78rem] text-neutral-900 dark:text-neutral-100 font-bold leading-snug">
                    {v.stuck_reason}
                  </div>
                </Link>
              ))}
            </div>
          </Card>
        )}

        {/* PHOTO PIPELINE widget — Photo Manager bottleneck at-a-glance */}
        {photos && (
          <Card
            className="p-4 mb-4 border-2 no-print"
            style={{
              borderColor: photos.totals.bottleneck > 0 ? "#b91c1c" : BRAND_BRONZE,
              background: `${BRAND_CREAM}44`,
            }}
            data-testid="posse-photo-widget"
          >
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Camera
                className={`w-4 h-4 ${photos.totals.bottleneck > 0 ? "text-red-700" : ""}`}
                style={photos.totals.bottleneck > 0 ? {} : { color: BRAND_BRONZE_DARK }}
                strokeWidth={2.4}
              />
              <div className="font-mono-label text-[0.65rem] tracking-widest" style={{ color: BRAND_BRONZE_DARK }}>
                PHOTO PIPELINE · PHOTO MANAGER
              </div>
              {photos.totals.bottleneck > 0 && (
                <div className="text-red-800 font-mono-label text-[0.6rem] tracking-widest flex items-center gap-1 ml-2">
                  <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2.4} />
                  {photos.totals.bottleneck} PAST 24HR · OLDEST {photos.totals.oldest_waiting_hours}H
                </div>
              )}
              <Link to="/photo-pipeline" className="ml-auto">
                <Button
                  size="sm"
                  className="h-8"
                  style={{ background: BRAND_BRONZE_DARK, color: "white" }}
                  data-testid="posse-photo-open"
                >
                  <Camera className="w-3.5 h-3.5 mr-1.5" strokeWidth={2.4} />
                  Open Photo Pipeline
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <button
                type="button"
                onClick={() => setPhotoStageOpen(photoStageOpen === "not_started" ? null : "not_started")}
                className={`rounded-md border p-2 text-center transition-all ${
                  photoStageOpen === "not_started"
                    ? "border-neutral-900 bg-neutral-100 dark:bg-neutral-800 ring-2 ring-neutral-900/20"
                    : "border-border bg-background hover:border-neutral-500"
                }`}
                data-testid="posse-photo-stage-awaiting"
              >
                <div className="font-mono-label text-[0.55rem] tracking-widest text-neutral-500">
                  AWAITING
                </div>
                <div className="font-display font-black text-2xl tabular-nums text-neutral-900 dark:text-white">
                  {photos.totals.not_started}
                </div>
                <div className="text-[0.55rem] text-neutral-500 mt-0.5">Tap to see cars</div>
              </button>
              <button
                type="button"
                onClick={() => setPhotoStageOpen(photoStageOpen === "in_progress" ? null : "in_progress")}
                className={`rounded-md border p-2 text-center transition-all ${
                  photoStageOpen === "in_progress"
                    ? "border-blue-700 bg-blue-100 dark:bg-blue-900/40 ring-2 ring-blue-700/30"
                    : "border-blue-300 bg-blue-50/70 dark:bg-blue-950/20 hover:border-blue-600"
                }`}
                data-testid="posse-photo-stage-shooting"
              >
                <div className="font-mono-label text-[0.55rem] tracking-widest text-blue-800">
                  SHOOTING
                </div>
                <div className="font-display font-black text-2xl tabular-nums text-blue-800">
                  {photos.totals.in_progress}
                </div>
                <div className="text-[0.55rem] text-blue-700 mt-0.5">Tap to see cars</div>
              </button>
              <button
                type="button"
                onClick={() => setPhotoStageOpen(photoStageOpen === "done" ? null : "done")}
                className={`rounded-md border p-2 text-center transition-all ${
                  photoStageOpen === "done"
                    ? "border-emerald-700 bg-emerald-100 dark:bg-emerald-900/40 ring-2 ring-emerald-700/30"
                    : "border-emerald-300 bg-emerald-50/70 dark:bg-emerald-950/20 hover:border-emerald-600"
                }`}
                data-testid="posse-photo-stage-ready"
              >
                <div className="font-mono-label text-[0.55rem] tracking-widest text-emerald-800">
                  READY
                </div>
                <div className="font-display font-black text-2xl tabular-nums text-emerald-800">
                  {photos.totals.done}
                </div>
                <div className="text-[0.55rem] text-emerald-700 mt-0.5">Tap to see cars</div>
              </button>
            </div>

            {/* Expandable stage list — shows every car in the tapped stage
                with stock #, model, waiting time, shots complete. Tap any car
                to jump to the Photo Pipeline page. */}
            {photoStageOpen && (
              <div
                className="rounded-lg border-2 border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-3 mb-3"
                data-testid={`posse-photo-stage-list-${photoStageOpen}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="font-mono-label text-[0.6rem] tracking-widest font-bold text-neutral-900 dark:text-white">
                    {photoStageOpen === "not_started" && "AWAITING · CARS WAITING FOR PHOTOS"}
                    {photoStageOpen === "in_progress" && "SHOOTING · PHOTOS IN PROGRESS"}
                    {photoStageOpen === "done" && "READY · PHOTOS COMPLETE"}
                  </div>
                  <button
                    onClick={() => setPhotoStageOpen(null)}
                    className="text-neutral-500 hover:text-neutral-900 dark:hover:text-white text-xs font-mono-label tracking-widest"
                    data-testid="posse-photo-stage-close"
                  >
                    CLOSE ✕
                  </button>
                </div>
                <div className="grid gap-2 max-h-72 overflow-y-auto">
                  {(photos.stages?.[photoStageOpen] || []).length === 0 && (
                    <div className="text-sm text-neutral-500 italic p-2 text-center">
                      No cars in this stage right now.
                    </div>
                  )}
                  {(photos.stages?.[photoStageOpen] || [])
                    .slice()
                    .sort((a, b) => (b.photo_hours_waiting || 0) - (a.photo_hours_waiting || 0))
                    .map((c) => (
                      <Link
                        key={c.id}
                        to="/photo-pipeline"
                        className="flex items-center gap-3 rounded-md border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50 p-2 hover:border-neutral-500 hover:bg-white dark:hover:bg-neutral-800 transition-all"
                        data-testid={`posse-photo-stage-car-${c.id}`}
                      >
                        <div className="w-10 h-10 rounded bg-neutral-900 grid place-items-center shrink-0">
                          <Camera
                            className={`w-4 h-4 ${
                              photoStageOpen === "done"
                                ? "text-emerald-400"
                                : photoStageOpen === "in_progress"
                                ? "text-blue-400"
                                : "text-neutral-400"
                            }`}
                            strokeWidth={2.2}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-sm text-neutral-900 dark:text-white truncate">
                            {c.year} {c.make_model}
                          </div>
                          <div className="text-[0.68rem] text-neutral-700 dark:text-neutral-300 font-mono">
                            Stock {c.stock_no} · {c.responsible_manager}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          {photoStageOpen === "done" ? (
                            <div className="font-display font-black text-lg tabular-nums text-emerald-600 leading-none">
                              {c.photo_shots_complete}/{c.photo_shots_total}
                            </div>
                          ) : (
                            <div
                              className={`font-display font-black text-lg tabular-nums leading-none ${
                                (c.photo_hours_waiting || 0) >= 24 ? "text-red-600" : "text-neutral-900 dark:text-white"
                              }`}
                            >
                              {c.photo_hours_waiting || 0}h
                            </div>
                          )}
                          <div className="text-[0.55rem] text-neutral-600 dark:text-neutral-400 font-mono-label tracking-widest mt-0.5">
                            {photoStageOpen === "done" ? "SHOTS" : "WAITING"}
                          </div>
                        </div>
                      </Link>
                    ))}
                </div>
              </div>
            )}

            {/* Oldest waiting car — the single most important number in this
                widget. GM sees exactly which car has been sitting the longest
                without photos so they can walk over and fix it right now. */}
            {(() => {
              const pool = [...(photos.stages?.not_started || []), ...(photos.stages?.in_progress || [])];
              const oldest = pool.length
                ? pool.reduce((a, b) => ((b.photo_hours_waiting || 0) > (a.photo_hours_waiting || 0) ? b : a))
                : null;
              if (!oldest) return null;
              return (
                <Link
                  to="/photo-pipeline"
                  className="flex items-center gap-3 rounded-lg border-2 border-red-500 bg-white dark:bg-neutral-900 p-3 hover:shadow-md transition-all"
                  data-testid="posse-photo-oldest"
                >
                  <div className="w-14 h-14 rounded-md bg-neutral-900 grid place-items-center shrink-0">
                    <Camera className="w-6 h-6 text-red-400" strokeWidth={2.2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono-label text-[0.55rem] tracking-widest text-red-700 font-bold">
                      OLDEST WAITING FOR PHOTOS
                    </div>
                    <div className="font-display font-black text-base text-neutral-900 dark:text-white leading-tight mt-0.5 truncate">
                      {oldest.year} {oldest.make_model}
                    </div>
                    <div className="text-[0.72rem] text-neutral-700 dark:text-neutral-300 font-mono font-semibold mt-0.5">
                      Stock {oldest.stock_no} · {oldest.responsible_manager}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-display font-black text-2xl tabular-nums text-red-600 leading-none">
                      {oldest.photo_hours_waiting}h
                    </div>
                    <div className="text-[0.6rem] text-neutral-600 dark:text-neutral-400 font-mono-label tracking-widest mt-0.5">
                      WAITING
                    </div>
                  </div>
                </Link>
              );
            })()}
          </Card>
        )}

        {/* NIGHTLY CHECK-IN prompt — daily accountability widget */}
        {checkinToday && (
          <Card
            className="p-4 mb-4 border-2 no-print"
            style={{
              borderColor:
                checkinToday.submitted_count === checkinToday.total ? "#059669" : "#b91c1c",
              background: `${BRAND_CREAM}44`,
            }}
            data-testid="posse-checkin-widget"
          >
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <ClipboardCheck
                className={`w-4 h-4 ${
                  checkinToday.submitted_count === checkinToday.total
                    ? "text-emerald-700"
                    : "text-red-700"
                }`}
                strokeWidth={2.4}
              />
              <div
                className="font-mono-label text-[0.65rem] tracking-widest"
                style={{ color: BRAND_BRONZE_DARK }}
              >
                TODAY&apos;S CHECK-IN · {checkinToday.submitted_count} / {checkinToday.total} MANAGERS IN
              </div>
              <Link to="/manager-checkin" className="ml-auto">
                <Button
                  size="sm"
                  className="h-8"
                  style={{ background: BRAND_BRONZE_DARK, color: "white" }}
                  data-testid="posse-checkin-open"
                >
                  <ClipboardCheck className="w-3.5 h-3.5 mr-1.5" strokeWidth={2.4} />
                  Open Check-In
                </Button>
              </Link>
            </div>
            <div className="flex flex-wrap gap-2">
              {checkinToday.deputies?.map((d) => {
                return (
                  <div
                    key={d.role}
                    className={`rounded-full px-3 py-1 border text-[0.65rem] flex items-center gap-1.5 ${
                      d.submitted
                        ? "border-emerald-500 bg-emerald-50 text-emerald-800"
                        : "border-amber-400 bg-amber-50 text-amber-900"
                    }`}
                    data-testid={`posse-checkin-chip-${d.role.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    {d.submitted ? "✓ " : "⏳ "}
                    <span className="font-semibold">{d.name}</span>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Cast of Characters removed — real names only, no callsigns. */}
        {false && data?.cast?.length > 0 && (
          <Card
            className="p-3 mb-4 border-2 no-print"
            style={{ borderColor: BRAND_BRONZE, background: `${BRAND_CREAM}55` }}
            data-testid="posse-cast"
          >
            <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
              <div className="flex items-center gap-2">
                <Star className="w-3.5 h-3.5" style={{ color: BRAND_BRONZE_DARK }} strokeWidth={2.4} />
                <div className="font-mono-label text-[0.6rem] tracking-widest" style={{ color: BRAND_BRONZE_DARK }}>
                  POSSE CAST · CALLSIGNS FOR EVERY DEPUTY IN THE LOOP
                </div>
              </div>
              {/* Clearance selector — controls what numbers show on click */}
              <div
                className="flex items-center gap-1.5 text-[0.65rem]"
                data-testid="posse-clearance-selector"
              >
                <span className="font-mono-label text-[0.55rem] tracking-widest" style={{ color: BRAND_BRONZE_DARK }}>
                  VIEWING AS
                </span>
                <select
                  value={clearance}
                  onChange={(e) => setClearanceLevel(e.target.value)}
                  className="h-7 rounded-md border border-[color:var(--posse-bronze)] bg-white px-2 text-[0.7rem] font-semibold"
                  data-testid="posse-clearance"
                >
                  {clearanceTiers.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {data.cast.map((c) => (
                <div
                  key={c.role}
                  className="rounded-full px-3 py-1 border text-[0.65rem] flex items-center gap-1.5"
                  style={{ borderColor: BRAND_BRONZE, background: "white" }}
                  data-testid={`posse-cast-${c.role.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <span className="font-mono-label text-[0.55rem] tracking-widest text-neutral-500">
                    {c.role.toUpperCase()}
                  </span>
                  <span className="font-semibold text-neutral-800">{c.name}</span>
                  <span className="italic" style={{ color: BRAND_BRONZE_DARK }}>
                    "{c.callsign}"
                  </span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* KPI strip — the money bleed metrics */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <Card className="p-4 border-border" data-testid="posse-kpi-pipeline">
            <div className="font-mono-label text-muted-foreground text-[0.6rem]">
              Cars in Pipeline
            </div>
            <div className="font-display font-black text-3xl tracking-tighter mt-1 tabular-nums">
              {totals.in_pipeline ?? "—"}
            </div>
            <div className="text-[0.65rem] text-muted-foreground mt-0.5">
              Acquired + Recon + Merch.
            </div>
          </Card>
          <Card
            className="p-4 border-border !bg-red-50 dark:!bg-red-950/20"
            data-testid="posse-kpi-tied"
          >
            <div className="font-mono-label text-red-700 dark:text-red-300 text-[0.6rem] flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" strokeWidth={2} />
              $ Tied Up (bleeding)
            </div>
            <div className="font-display font-black text-2xl tracking-tighter mt-1 tabular-nums text-red-700 dark:text-red-300">
              {money(totals.total_tied_up)}
            </div>
            <div className="text-[0.6rem] text-red-700/70 dark:text-red-300/70 mt-0.5">
              acquired + recon so far
            </div>
          </Card>
          <Card className="p-4 border-border" data-testid="posse-kpi-days">
            <div className="font-mono-label text-muted-foreground text-[0.6rem]">
              Avg Days in Recon
            </div>
            <div className="font-display font-black text-3xl tracking-tighter mt-1 tabular-nums">
              {totals.avg_days_in_recon ?? "—"} d
            </div>
            <div className="text-[0.6rem] text-muted-foreground mt-0.5">
              oldest: {totals.oldest_days ?? 0}d
            </div>
          </Card>
          <Card
            className="p-4 border-border"
            style={{ background: BRAND_CREAM, borderColor: BRAND_BRONZE }}
            data-testid="posse-kpi-frontline"
          >
            <div className="font-mono-label text-[0.6rem]" style={{ color: BRAND_BRONZE_DARK }}>
              Front Line Ready
            </div>
            <div
              className="font-display font-black text-3xl tracking-tighter mt-1 tabular-nums"
              style={{ color: BRAND_BRONZE_DARK }}
            >
              {totals.front_line ?? 0}
            </div>
            <div className="text-[0.65rem] mt-0.5" style={{ color: BRAND_BRONZE_DARK }}>
              live on the lot
            </div>
          </Card>
          <Card
            className="p-4 border-border !bg-emerald-50 dark:!bg-emerald-950/20"
            data-testid="posse-kpi-gross"
          >
            <div className="font-mono-label text-emerald-800 dark:text-emerald-300 text-[0.6rem]">
              Projected Pipeline Gross
            </div>
            <div className="font-display font-black text-2xl tracking-tighter mt-1 tabular-nums text-emerald-800 dark:text-emerald-200">
              {money(totals.projected_gross_pipeline)}
            </div>
            <div className="text-[0.6rem] text-emerald-700/70 dark:text-emerald-300/70 mt-0.5">
              sold this mo: {money(totals.sold_gross_month)}
            </div>
          </Card>
        </div>

        {/* Manager Compliance — accountability strip. Whole app dies if managers don't update. */}
        {compliance.length > 0 && (
          <Card
            className="p-5 mb-6 border-2"
            style={{ borderColor: BRAND_BRONZE_DARK, background: `${BRAND_CREAM}66` }}
            data-testid="posse-compliance"
          >
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-red-600" strokeWidth={2.2} />
              <div className="font-mono-label text-[0.65rem] tracking-widest" style={{ color: BRAND_BRONZE_DARK }}>
                MANAGER UPDATE SCORE · WHO KEPT THE BOARD FRESH?
              </div>
            </div>
            <p className="text-xs text-neutral-700 mb-4 max-w-4xl leading-relaxed">
              Each manager should tap in and update their cars by 8pm — and every time a
              vehicle moves stages. Green means fresh, red means overdue. The GM sees this board.
            </p>
            <div className="grid md:grid-cols-3 gap-3">
              {compliance.map((m) => {
                const key = m.name.toLowerCase().includes("recon")
                  ? "recon"
                  : m.name.toLowerCase().includes("body")
                  ? "body"
                  : "used_car";
                const rem = reminders[key];
                return (
                  <div
                    key={m.name}
                    className={`rounded-lg p-4 border-2 ${
                      m.compliance_pct >= 90
                        ? "border-emerald-400 bg-emerald-50 dark:bg-emerald-950/20"
                        : m.compliance_pct >= 70
                        ? "border-yellow-400 bg-yellow-50 dark:bg-yellow-950/20"
                        : "border-red-400 bg-red-50 dark:bg-red-950/20"
                    }`}
                    data-testid={`posse-compliance-${key}`}
                  >
                    <div className="font-display font-bold text-sm">{m.name}</div>
                    <div className="flex items-baseline gap-2 mt-1">
                      <div className="font-display font-black text-3xl tabular-nums">
                        {m.compliance_pct}%
                      </div>
                      <div
                        className={`text-[0.6rem] font-semibold ${
                          m.compliance_pct >= 90
                            ? "text-emerald-700"
                            : m.compliance_pct >= 70
                            ? "text-yellow-800"
                            : "text-red-700"
                        }`}
                      >
                        compliance
                      </div>
                    </div>
                    <div className="flex justify-between mt-2 text-[0.65rem] tabular-nums">
                      <span className="text-emerald-700">✓ {m.fresh_24h} fresh</span>
                      <span className="text-red-700">! {m.stale_24h} overdue</span>
                      <span className="text-muted-foreground">{m.total} total</span>
                    </div>
                    {m.overdue_cars?.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-black/10 space-y-0.5">
                        <div className="font-mono-label text-[0.5rem] text-red-800 tracking-widest">
                          OLDEST OVERDUE
                        </div>
                        {m.overdue_cars.slice(0, 2).map((c) => (
                          <div
                            key={c.id}
                            className="text-[0.6rem] flex justify-between items-baseline"
                          >
                            <span className="truncate">{c.label}</span>
                            <span className="text-red-700 font-bold tabular-nums shrink-0 ml-1">
                              {c.hours_since_touch}h
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    {rem?.message && (
                      <div className="mt-3 pt-2 border-t border-black/10">
                        <div className="font-mono-label text-[0.5rem] text-neutral-700 tracking-widest mb-1">
                          📱 8:00 PM auto-reminder
                        </div>
                        <div className="rounded bg-white/70 dark:bg-neutral-900/40 border border-black/5 p-2 text-[0.6rem] leading-relaxed font-mono">
                          {rem.message}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* GM Daily Digest — three text drops that hit the GM's phone every day */}
        <Card
          className="p-5 mb-6 border-2 no-print"
          style={{ borderColor: BRAND_BRONZE, background: `${BRAND_CREAM}44` }}
          data-testid="posse-gm-digest"
        >
          <div className="flex items-center gap-2 mb-3">
            <Bell className="w-4 h-4" style={{ color: BRAND_BRONZE_DARK }} strokeWidth={2.2} />
            <div className="font-mono-label text-[0.65rem] tracking-widest" style={{ color: BRAND_BRONZE_DARK }}>
              GM DAILY DIGEST · 3x A DAY, AUTOMATIC
            </div>
          </div>
          <p className="text-xs text-neutral-700 mb-4 max-w-3xl leading-relaxed">
            The GM&apos;s phone lights up three times daily — <b>7am · Noon · 6pm</b> — with
            pipeline status, cars hitting front line today, and red-flagged aging inventory.
            No hunting reports. No login. Just the number he needs, when he needs it.
          </p>
          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
            {[
              { key: "morning", label: "7:00 AM · Kickoff", accent: "bg-orange-500 text-white", Icon: Sunrise },
              { key: "midday", label: "12:00 PM · Midday", accent: "bg-blue-600 text-white", Icon: Sun },
              { key: "evening", label: "6:00 PM · Close-out", accent: "bg-indigo-700 text-white", Icon: Moon },
            ].map(({ key, label, accent, Icon }) => (
              <div key={key} className="flex flex-col items-center" data-testid={`posse-digest-${key}`}>
                <div className="flex items-center gap-1.5 mb-2 font-mono-label text-[0.6rem] tracking-widest text-neutral-700">
                  <Icon className="w-3.5 h-3.5" strokeWidth={2} />
                  {label}
                </div>
                <PhoneMock
                  dealer={dealer}
                  from="+1 (913) 555-0100 · PreOwned Posse"
                  body={digests[key]?.message || "Loading…"}
                  sent={label.split(" · ")[0]}
                  label={`Posse Digest · ${label.split(" · ")[1]}`}
                  accent={accent}
                  testid={`posse-digest-phone-${key}`}
                />
              </div>
            ))}
            {/* 4th slot — the compliance-alert SMS the GM gets at 8:15pm
                naming which managers didn't update their side of the board */}
            <div className="flex flex-col items-center" data-testid="posse-digest-compliance">
              <div className="flex items-center gap-1.5 mb-2 font-mono-label text-[0.6rem] tracking-widest text-red-700">
                <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2} />
                8:15 PM · Compliance Alert
              </div>
              <PhoneMock
                dealer={dealer}
                from="+1 (913) 555-0100 · PreOwned Posse"
                body={complianceText?.message || "Loading…"}
                sent="8:15 PM"
                label={
                  complianceText?.count
                    ? `Posse · ${complianceText.count} manager${complianceText.count === 1 ? "" : "s"} behind`
                    : "Posse · All clear tonight"
                }
                accent={
                  complianceText?.count
                    ? "bg-red-600 text-white"
                    : "bg-emerald-600 text-white"
                }
                testid="posse-digest-phone-compliance"
              />
              <div className="text-[0.55rem] text-neutral-600 mt-1 max-w-[15rem] text-center italic">
                Fires only when a manager hasn't updated — silence means everyone's on it.
              </div>
            </div>
          </div>
        </Card>

        {/* Two demo weapons: GM alert + Salesman SMS lookup */}
        <div className="grid md:grid-cols-2 gap-3 mb-6 no-print">
          {latestFL && (
            <Card
              className="p-4 border-l-4"
              style={{
                borderLeftColor: BRAND_BRONZE,
                background: `${BRAND_CREAM}55`,
              }}
              data-testid="posse-gm-alert"
            >
              <div className="flex items-start gap-3">
                <div
                  className="w-9 h-9 rounded-full grid place-items-center shrink-0"
                  style={{ background: BRAND_BRONZE, color: "white" }}
                >
                  <Bell className="w-4 h-4" strokeWidth={2.2} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-mono-label text-[0.6rem] tracking-widest" style={{ color: BRAND_BRONZE_DARK }}>
                    🚨 GM ALERT · Just Hit Front Line
                  </div>
                  <div className="font-display font-black text-lg tracking-tight mt-1">
                    {latestFL.year} {latestFL.make_model}
                  </div>
                  <div className="text-xs text-neutral-700 mt-1 leading-relaxed">
                    Front-line cost <b>{money(latestFL.front_line_cost)}</b> · Target{" "}
                    <b>{money(latestFL.target_retail)}</b> · Projected gross{" "}
                    <b className="text-emerald-700">{money(latestFL.projected_gross)}</b>
                  </div>
                  <div className="text-[0.6rem] text-neutral-600 mt-2 italic">
                    → auto-texted to GM the second it went live
                  </div>
                </div>
              </div>
            </Card>
          )}

          <Card className="p-4 border-border" data-testid="posse-sms-lookup">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-blue-600 grid place-items-center shrink-0">
                <MessageSquareText className="w-4 h-4 text-white" strokeWidth={2.2} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-mono-label text-[0.6rem] tracking-widest text-blue-800 dark:text-blue-300">
                  TEXT A VIN TO CHECK A CAR
                </div>
                <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  Salesperson on the lot needs to know if a car is ready to sell? They text
                  the last 6 of the VIN and get the answer back in seconds — no phone calls,
                  no walking to recon. Try it below:
                </div>
                <div className="flex gap-2 mt-2">
                  <Input
                    placeholder="Last 6 of VIN (e.g. 5F3K7A)"
                    value={lookupVin}
                    onChange={(e) => setLookupVin(e.target.value.toUpperCase())}
                    className="h-9 font-mono"
                    onKeyDown={(e) => e.key === "Enter" && doLookup()}
                    data-testid="posse-lookup-input"
                  />
                  <Button size="sm" onClick={doLookup} data-testid="posse-lookup-btn" className="bg-blue-600 hover:bg-blue-700 text-white">
                    <Search className="w-4 h-4" strokeWidth={2} />
                  </Button>
                </div>
                {lookupResp && (
                  <div
                    className={`mt-2 p-2 rounded-md text-xs font-mono leading-relaxed ${
                      lookupResp.found
                        ? "bg-emerald-50 border border-emerald-200 text-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200"
                        : "bg-red-50 border border-red-200 text-red-800"
                    }`}
                    data-testid="posse-lookup-result"
                  >
                    {lookupResp.message}
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>

        {/* Kanban */}
        <Card
          className="p-5 mb-6 border-2 border-red-400 bg-red-50/70 dark:bg-red-950/20 no-print"
          data-testid="posse-flag-widget"
        >
          <div className="flex items-center gap-2 mb-2">
            <Flag className="w-4 h-4 text-red-700" strokeWidth={2.2} />
            <div className="font-mono-label text-[0.65rem] tracking-widest text-red-800 dark:text-red-300">
              FLAG A FRONT-LINE CAR · ANYONE ON THE LOT CAN
            </div>
          </div>
          <p className="text-xs text-neutral-700 mb-3 max-w-4xl leading-relaxed">
            Salesman spots a loose mirror, a stain, a low tire on a Front Line car. One text.
            Recon Manager gets it instantly and pulls the car back in. No hunting, no phone tag,
            no lost customer.
          </p>
          <div className="grid md:grid-cols-[130px_1fr_180px_auto] gap-2">
            <Input
              placeholder="VIN last 6"
              value={flagForm.vin}
              onChange={(e) => setFlagForm({ ...flagForm, vin: e.target.value.toUpperCase() })}
              className="font-mono h-9"
              data-testid="posse-flag-vin"
            />
            <Input
              placeholder="What's wrong? (e.g. stain on driver seat)"
              value={flagForm.issue}
              onChange={(e) => setFlagForm({ ...flagForm, issue: e.target.value })}
              className="h-9"
              data-testid="posse-flag-issue"
            />
            <Input
              placeholder="Your name (optional)"
              value={flagForm.reporter}
              onChange={(e) => setFlagForm({ ...flagForm, reporter: e.target.value })}
              className="h-9"
              data-testid="posse-flag-reporter"
            />
            <Button
              onClick={submitFlag}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="posse-flag-submit"
            >
              <Flag className="w-4 h-4 mr-1.5" strokeWidth={2} />
              Send Flag
            </Button>
          </div>
          {flagResult?.auto_text && (
            <div className="mt-3 rounded-md bg-white/80 dark:bg-neutral-900/60 border border-red-200 p-3 text-xs font-mono leading-relaxed" data-testid="posse-flag-preview">
              <div className="font-mono-label text-[0.55rem] text-red-800 tracking-widest mb-1">
                📱 Auto-text sent to Recon Manager
              </div>
              {flagResult.auto_text}
            </div>
          )}
          {flags.length > 0 && (
            <div className="mt-4 pt-3 border-t border-red-200">
              <div className="font-mono-label text-[0.55rem] text-red-800 tracking-widest mb-2">
                RECENT FLAGS (LAST 10)
              </div>
              <div className="space-y-1">
                {flags.slice(0, 4).map((f) => (
                  <div
                    key={f.id}
                    className="flex items-baseline gap-2 text-[0.65rem]"
                    data-testid={`posse-flag-row-${f.id}`}
                  >
                    <span className="font-mono font-bold text-red-800">…{f.vin_last6}</span>
                    <span className="flex-1 truncate">{f.issue}</span>
                    <span className="text-muted-foreground italic shrink-0">— {f.reporter}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>

        {/* MASTER TEXT — one accountability broadcast to every deputy */}
        {master && (
          <Card
            className="p-5 mb-6 border-2 no-print"
            style={{ borderColor: BRAND_BRONZE_DARK, background: "#0f172a" }}
            data-testid="posse-master-text"
          >
            <div className="flex items-center gap-2 mb-2">
              <MessageSquareText className="w-4 h-4 text-yellow-300" strokeWidth={2.4} />
              <div className="font-mono-label text-[0.65rem] tracking-widest text-yellow-300">
                POSSE MASTER TEXT · ONE BROADCAST TO EVERY DEPUTY
              </div>
              {masterSentAt && (
                <div className="ml-auto text-[0.6rem] text-white/70 italic">
                  Last sent {new Date(masterSentAt).toLocaleString()}
                </div>
              )}
            </div>
            <p className="text-xs text-white/70 mb-4 max-w-4xl leading-relaxed">
              One tap · every deputy&apos;s phone lights up with the same story:
              pipeline count, past-due count, who&apos;s behind, and the exact reply format
              required by 8pm. No hunting. No excuses. Everyone hears the bell at once.
            </p>
            <div className="grid md:grid-cols-[1fr_auto] gap-4 items-start">
              {/* Message preview */}
              <div
                className="rounded-lg p-4 border font-mono text-[0.8rem] leading-relaxed"
                style={{ background: "#f6ecd8", borderColor: BRAND_BRONZE, color: "#1a1a1a" }}
                data-testid="posse-master-text-preview"
              >
                {master.body}
              </div>
              <div className="flex flex-col gap-2">
                <Button
                  onClick={sendMaster}
                  disabled={sendingMaster}
                  className="bg-yellow-400 hover:bg-yellow-300 text-neutral-900 font-bold h-12 px-6"
                  data-testid="posse-master-text-send"
                >
                  <MessageSquareText className="w-4 h-4 mr-2" strokeWidth={2.4} />
                  {sendingMaster
                    ? "Sending…"
                    : `Send to ${master.recipients?.length || 0} deputies`}
                </Button>
                <div className="text-[0.55rem] text-white/60 italic text-center max-w-[180px]">
                  Wires to Twilio the moment Trust Hub KYC clears — logged as mock for now.
                </div>
              </div>
            </div>

            {/* Recipient roster — real names only */}
            <div className="mt-4 pt-3 border-t border-white/10">
              <div className="font-mono-label text-[0.55rem] tracking-widest text-yellow-200 mb-2">
                RECIPIENT ROSTER · {master.recipients?.length || 0} MANAGERS
              </div>
              <div className="flex flex-wrap gap-2">
                {(master.recipients || []).map((r) => (
                  <div
                    key={r.role}
                    className="rounded-full px-3 py-1 border text-[0.65rem] flex items-center gap-1.5"
                    style={{ borderColor: BRAND_BRONZE, background: "#1c1c1c", color: "#f6ecd8" }}
                    data-testid={`posse-master-recipient-${r.role.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    <span className="font-mono-label text-[0.55rem] tracking-widest text-yellow-200">
                      {r.role.toUpperCase()}
                    </span>
                    <span className="font-semibold">{r.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}

        {/* Recon Race — a horizontal race track showing every car's
            progress toward the "Front Line Ready" checkered flag. Sold cars
            park in Victory Lane. A wave of checker flags flutters overhead. */}
        <Card
          className="p-4 mb-6 border-2 no-print overflow-hidden"
          style={{ borderColor: BRAND_BRONZE_DARK, background: "#0a0a0a" }}
          data-testid="posse-speedway"
        >
          <div className="flex items-center gap-2 mb-3">
            <Flag className="w-4 h-4" style={{ color: "#f6ecd8" }} strokeWidth={2.4} />
            <div className="font-mono-label text-[0.65rem] tracking-widest text-[#f6ecd8]">
              RECON RACE · EVERY CAR ON THE TRACK
            </div>
            <div className="ml-auto text-[0.6rem] text-white/60 italic">
              Hover a car for details · red = past due (&gt;7d)
            </div>
          </div>

          {/* Wave of checker flags — bunting fluttering above the track */}
          <div className="relative h-8 mb-1 select-none" aria-hidden data-testid="posse-flag-wave">
            {Array.from({ length: 18 }).map((_, i) => {
              const left = (i / 17) * 100;
              const wave = Math.sin(i * 0.9) * 6 + 8; // vertical wave
              const tilt = Math.sin(i * 0.9 + 1) * 12; // gentle sway
              return (
                <div
                  key={i}
                  className="absolute origin-top"
                  style={{
                    left: `${left}%`,
                    top: `${wave}px`,
                    transform: `translateX(-50%) rotate(${tilt}deg)`,
                    animation: `flagFlutter${i % 3} 1.6s ease-in-out ${i * 0.08}s infinite alternate`,
                  }}
                >
                  <svg width="18" height="14" viewBox="0 0 12 10">
                    {/* pole */}
                    <rect x="0" y="0" width="0.8" height="10" fill="#a06a1e" />
                    {/* 2x2 checker */}
                    <rect x="1" y="0.5" width="5" height="4" fill="#f6ecd8" />
                    <rect x="1" y="0.5" width="2.5" height="2" fill="#000" />
                    <rect x="3.5" y="2.5" width="2.5" height="2" fill="#000" />
                  </svg>
                </div>
              );
            })}
            <style>{`
              @keyframes flagFlutter0 { from { transform: translateX(-50%) rotate(-6deg); } to { transform: translateX(-50%) rotate(8deg); } }
              @keyframes flagFlutter1 { from { transform: translateX(-50%) rotate(4deg); } to { transform: translateX(-50%) rotate(-10deg); } }
              @keyframes flagFlutter2 { from { transform: translateX(-50%) rotate(-9deg); } to { transform: translateX(-50%) rotate(5deg); } }
            `}</style>
          </div>

          {/* Track shell */}
          <div className="relative rounded-lg overflow-hidden" style={{ background: "#1c1c1c" }}>
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, #262626 0%, #1a1a1a 40%, #1a1a1a 60%, #262626 100%)",
              }}
            />
            <div
              className="absolute inset-x-0 top-1/2 h-[2px] opacity-70"
              style={{
                background:
                  "repeating-linear-gradient(90deg, #f6ecd8 0 22px, transparent 22px 44px)",
                transform: "translateY(-1px)",
              }}
            />
            {/* Checkpoint markers */}
            <div className="relative grid grid-cols-4 text-[0.55rem] font-mono-label tracking-widest text-white/60 px-3 pt-2">
              <div>ACQUIRED</div>
              <div>IN RECON</div>
              <div>MERCHANDISING</div>
              <div className="text-right text-[#f6ecd8]">🏁 FRONT LINE</div>
            </div>

            {/* Cars — placed by stage + within-stage jitter */}
            <div className="relative h-[220px]">
              {(data?.vehicles || [])
                .filter((v) => v.status !== "sold")
                .map((v, i) => {
                  const stageBase = {
                    acquired: 4,
                    in_recon: 30,
                    merchandising: 56,
                    front_line: 88,
                  }[v.status] ?? 4;
                  const jitter =
                    v.status === "front_line"
                      ? (i % 5) * 1.2
                      : Math.min(18, (v.days_in_recon || 0) * 1.4);
                  const left = Math.min(94, stageBase + jitter);
                  const lane = i % 3;
                  const top = 30 + lane * 55;
                  const isPastDue = v.days_in_recon > 7;
                  const isFinished = v.status === "front_line";
                  return (
                    <div
                      key={v.id}
                      className="absolute group cursor-help transition-transform hover:scale-125"
                      style={{ left: `${left}%`, top: `${top}px` }}
                      title={`${v.stock_no} · ${v.year} ${v.make_model} · ${v.responsible_manager} · ${v.days_in_recon}d`}
                      data-testid={`posse-speedway-car-${v.id}`}
                    >
                      <Car
                        className={`w-5 h-5 drop-shadow-lg ${
                          isFinished
                            ? "text-emerald-400"
                            : isPastDue
                            ? "text-red-500 animate-pulse"
                            : "text-[#f6ecd8]"
                        }`}
                        strokeWidth={2.4}
                      />
                      <div className="absolute left-1/2 -translate-x-1/2 -bottom-4 text-[0.5rem] font-mono text-white/50 whitespace-nowrap">
                        {v.days_in_recon}d
                      </div>
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity absolute left-1/2 -translate-x-1/2 -top-14 bg-[#f6ecd8] text-neutral-900 text-[0.6rem] rounded-md px-2 py-1 shadow-xl whitespace-nowrap z-10 pointer-events-none">
                        <div className="font-mono font-bold">{v.stock_no}</div>
                        <div className="italic text-[0.55rem]">
                          {v.year} {v.make_model}
                        </div>
                        <div className="text-[0.55rem] mt-0.5">
                          {v.responsible_manager}
                        </div>
                      </div>
                    </div>
                  );
                })}
              {/* Checkered finish-line pole */}
              <div
                className="absolute top-0 bottom-0 w-[8px]"
                style={{
                  right: "5%",
                  background:
                    "repeating-linear-gradient(0deg, #ffffff 0 10px, #000 10px 20px)",
                  boxShadow: "0 0 20px rgba(255,255,255,0.45)",
                }}
                aria-hidden
              />
            </div>
          </div>

          {/* Victory Lane — sold cars */}
          {(data?.vehicles || []).some((v) => v.status === "sold") && (
            <div
              className="mt-3 rounded-lg px-3 py-2 flex items-center gap-3 overflow-x-auto"
              style={{
                background:
                  "linear-gradient(90deg, #7a4e0f 0%, #a06a1e 55%, #c98c3d 100%)",
              }}
              data-testid="posse-speedway-victory"
            >
              <div className="flex items-center gap-1.5 shrink-0">
                <Trophy className="w-4 h-4 text-yellow-200" strokeWidth={2.4} />
                <span className="font-mono-label text-[0.6rem] tracking-widest text-yellow-100">
                  VICTORY LANE
                </span>
              </div>
              <div className="flex gap-2">
                {(data?.vehicles || [])
                  .filter((v) => v.status === "sold")
                  .map((v) => (
                    <div
                      key={v.id}
                      className="shrink-0 rounded-full bg-black/40 px-2 py-1 text-[0.6rem] text-yellow-50 border border-yellow-200/40 whitespace-nowrap"
                      title={`${v.stock_no} · sold gross ${money(v.sold_gross)}`}
                      data-testid={`posse-victory-${v.id}`}
                    >
                      <Car className="w-3 h-3 inline mr-1 -mt-0.5" strokeWidth={2.4} />
                      {v.stock_no} · {money(v.sold_gross)}
                    </div>
                  ))}
              </div>
            </div>
          )}
        </Card>

        {/* Kanban */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3 mb-6 no-print" data-testid="posse-kanban">
          {COLUMNS.map((col) => {
            const list = columns[col.key] || [];
            return (
              <div key={col.key} className="min-w-0" data-testid={`posse-col-${col.key}`}>
                <div className={`px-3 py-2 rounded-t-md flex items-center gap-2 ${col.tint}`}>
                  <col.Icon className="w-4 h-4" strokeWidth={2.2} />
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-sm truncate">{col.title}</div>
                    <div className="text-[0.55rem] opacity-85 truncate">{col.sub}</div>
                  </div>
                  <div className="font-display font-black text-lg tabular-nums">{list.length}</div>
                </div>
                <div className="border border-t-0 border-border rounded-b-md p-2 bg-muted/20 space-y-2 min-h-[240px]">
                  {list.length === 0 && (
                    <div className="text-xs text-muted-foreground text-center py-8">
                      Empty stage.
                    </div>
                  )}
                  {list.map((v) => {
                    const isPipeline = ["acquired", "in_recon", "merchandising"].includes(v.status);
                    return (
                      <div
                        key={v.id}
                        onClick={() => setActiveVehicle(v)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setActiveVehicle(v);
                          }
                        }}
                        className={`rounded-md p-3 border border-border bg-background shadow-sm relative overflow-hidden cursor-pointer transition-all ${
                          isPipeline ? ageStyle(v.days_in_recon) : ""
                        } ${
                          v.all_green
                            ? "ring-4 ring-emerald-500 shadow-lg shadow-emerald-200/60 hover:shadow-emerald-300/70"
                            : isPipeline &&
                              (v.days_in_recon > 7 ||
                                (v.status_lights || []).some((l) => !l.green))
                            ? "ring-2 ring-red-500 shadow-md shadow-red-200/40 hover:shadow-red-300/50"
                            : "hover:shadow-md hover:border-[color:var(--posse-bronze)]"
                        }`}
                        data-testid={`posse-card-${v.id}`}
                      >
                        {v.all_green && (
                          <>
                            {/* Gold trophy corner badge */}
                            <div
                              className="absolute top-1 right-1 flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[0.5rem] font-black tracking-widest z-10"
                              style={{
                                background: "linear-gradient(135deg, #34d399, #10b981)",
                                color: "#052e16",
                                boxShadow: "0 0 8px rgba(16,185,129,0.7)",
                              }}
                              data-testid={`posse-allgreen-${v.id}`}
                            >
                              <Star className="w-2.5 h-2.5" strokeWidth={2.6} fill="#052e16" />
                              ALL-GREEN
                            </div>
                            {/* Confetti dots — 8 pieces bursting from the star */}
                            <div className="pointer-events-none absolute inset-0" aria-hidden>
                              {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                                <span
                                  key={i}
                                  className="absolute top-3 right-6 w-1 h-1 rounded-full"
                                  style={{
                                    background: ["#10b981", "#34d399", "#facc15", "#059669", "#22c55e"][i % 5],
                                    animation: `posseConfetti 1.4s ease-out ${i * 0.08}s 1 forwards`,
                                    transform: `rotate(${i * 45}deg) translateY(-2px)`,
                                  }}
                                />
                              ))}
                            </div>
                          </>
                        )}
                        {!v.all_green && isPipeline &&
                          (v.days_in_recon > 7 ||
                            (v.status_lights || []).some((l) => !l.green)) && (
                            <div
                              className="absolute top-1 right-1 flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[0.5rem] font-black tracking-widest z-10"
                              style={{
                                background: "linear-gradient(135deg, #ef4444, #b91c1c)",
                                color: "#fef2f2",
                                boxShadow: "0 0 8px rgba(239,68,68,0.6)",
                              }}
                              data-testid={`posse-problem-${v.id}`}
                            >
                              <AlertTriangle className="w-2.5 h-2.5" strokeWidth={2.6} />
                              PROBLEM
                            </div>
                          )}
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="font-display font-bold text-sm truncate">
                              {v.year} {v.make_model}
                            </div>
                            <div className="text-[0.6rem] text-muted-foreground font-mono">
                              VIN …{v.vin_last6}
                            </div>
                          </div>
                          {isPipeline && (
                            <div className="flex flex-col items-end shrink-0">
                              <span className={`w-2 h-2 rounded-full ${ageDot(v.days_in_recon)}`} aria-hidden />
                              <div className="font-mono-label text-[0.5rem] text-muted-foreground mt-0.5 tabular-nums">
                                {v.days_in_recon}d
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Money summary */}
                        <div className="mt-2 grid grid-cols-2 gap-x-2 gap-y-0.5 text-[0.6rem] tabular-nums">
                          <div className="text-muted-foreground">Acquired</div>
                          <div className="text-right font-semibold">{money(v.acquired_cost)}</div>
                          <div className="text-muted-foreground">Recon so far</div>
                          <div className="text-right font-semibold text-orange-700">
                            {money(v.recon_committed)}
                          </div>
                          <div className="text-muted-foreground">Front-line</div>
                          <div className="text-right font-semibold">{money(v.front_line_cost)}</div>
                          <div className="text-muted-foreground">Target retail</div>
                          <div className="text-right font-semibold">{money(v.target_retail)}</div>
                          {isPipeline && v.eta_days_to_frontline > 0 && (
                            <>
                              <div className="text-muted-foreground font-mono-label text-[0.55rem] tracking-widest">
                                ETA · Front Line
                              </div>
                              <div
                                className={`text-right font-bold tabular-nums ${
                                  v.eta_days_to_frontline <= 2
                                    ? "text-emerald-700"
                                    : v.eta_days_to_frontline <= 5
                                    ? "text-yellow-700"
                                    : "text-red-700"
                                }`}
                              >
                                {v.eta_days_to_frontline}d
                              </div>
                            </>
                          )}
                          <div className="font-mono-label text-[0.55rem] text-emerald-800 tracking-widest col-span-1 mt-0.5">
                            {v.status === "sold" ? "SOLD GROSS" : "PROJ. GROSS"}
                          </div>
                          <div className="text-right font-black text-emerald-700 col-span-1 mt-0.5">
                            {money(v.sold_gross ?? v.projected_gross)}
                          </div>
                        </div>

                        {/* Recon line items */}
                        {v.recon_items?.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-dashed border-border space-y-0.5">
                            {v.recon_items.map((it, idx) => {
                              const Icon = RECON_ICON[it.category] || Wrench;
                              const clr =
                                it.status === "complete"
                                  ? "text-emerald-600"
                                  : it.status === "in_progress"
                                  ? "text-yellow-700"
                                  : "text-muted-foreground line-through";
                              return (
                                <div key={idx} className="flex items-center justify-between text-[0.55rem]">
                                  <span className={`inline-flex items-center gap-1 ${clr}`}>
                                    <Icon className="w-2.5 h-2.5" strokeWidth={2} />
                                    {it.category}
                                  </span>
                                  <span className={`font-mono tabular-nums ${clr}`}>
                                    {money(it.cost)}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {v.salesman && (
                          <div className="mt-2 text-[0.6rem] text-muted-foreground italic">
                            Assigned: {v.salesman}
                          </div>
                        )}
                        {/* Traffic-light status row — GM's "at a glance" view · CLICK TO TOGGLE */}
                        {v.status_lights && (
                          <div
                            className="mt-2 pt-2 border-t border-dashed border-border flex items-center justify-between gap-1"
                            data-testid={`posse-lights-${v.id}`}
                          >
                            {v.status_lights.map((l) => (
                              <button
                                key={l.key}
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleLight(v.id, l.key, !l.green);
                                }}
                                className="flex flex-col items-center gap-0.5 flex-1 hover:scale-110 active:scale-95 transition-transform focus:outline-none focus:ring-2 focus:ring-yellow-400 rounded"
                                title={`Click to mark ${l.label} ${l.green ? "not done" : "done"}`}
                                data-testid={`posse-light-${v.id}-${l.key}`}
                              >
                                <span
                                  className={`w-3 h-3 rounded-full ${
                                    l.green
                                      ? "bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.7)]"
                                      : "bg-red-500 shadow-[0_0_6px_rgba(239,68,68,0.7)] animate-pulse"
                                  }`}
                                  aria-hidden
                                />
                                <span className="text-[0.5rem] font-mono-label tracking-widest text-neutral-500 truncate w-full text-center">
                                  {l.label}
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                        {/* Last-touched audit stamp — enforces accountability */}
                        <div
                          className={`mt-2 pt-2 border-t border-dashed border-border flex items-center justify-between text-[0.55rem] ${
                            v.is_stale ? "text-red-700" : "text-muted-foreground"
                          }`}
                        >
                          <span className="truncate">
                            {v.is_stale ? "⚠ " : "✓ "}
                            {v.last_touched_by || "—"}
                          </span>
                          <span className="tabular-nums font-mono shrink-0 ml-1">
                            {v.hours_since_touch}h ago
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Print-only inventory table — renders only when the user clicks
            "Print Inventory" in the header. Kanban is no-print so this stays clean. */}
        <div className="hidden print:block" data-testid="posse-print-inventory">
          <div className="font-display font-black text-xl mb-1">Preowned Posse Inventory</div>
          <div className="text-[0.65rem] text-neutral-600 mb-3">
            {data?.vehicles?.length || 0} vehicles · generated {new Date().toLocaleString()}
          </div>
          <table className="w-full text-[0.7rem]">
            <thead
              className="text-left"
              style={{ background: BRAND_CREAM, color: BRAND_BRONZE_DARK }}
            >
              <tr className="font-mono text-[0.55rem] tracking-widest">
                <th className="p-1">Stock #</th>
                <th className="p-1">VIN</th>
                <th className="p-1">Year / Make / Model</th>
                <th className="p-1">Stage</th>
                <th className="p-1 text-right">Days</th>
                <th className="p-1">Manager</th>
                <th className="p-1">Acquired</th>
                <th className="p-1">Target Front-Line</th>
                <th className="p-1">Pic · Det · Dnt · Wsh · Svc</th>
              </tr>
            </thead>
            <tbody>
              {(data?.vehicles || []).map((v) => (
                <tr key={v.id} className="border-t border-black/10">
                  <td className="p-1 font-mono">{v.stock_no}</td>
                  <td className="p-1 font-mono">…{v.vin_last6}</td>
                  <td className="p-1">
                    {v.year} {v.make_model}
                  </td>
                  <td className="p-1 capitalize">{(v.status || "").replace("_", " ")}</td>
                  <td className="p-1 text-right tabular-nums">{v.days_in_recon}</td>
                  <td className="p-1">
                    {v.responsible_manager}
                  </td>
                  <td className="p-1 font-mono">{v.acquired_on}</td>
                  <td className="p-1 font-mono">{v.target_frontline_date || "—"}</td>
                  <td className="p-1">
                    <span className="inline-flex gap-1 items-center">
                      {(v.status_lights || []).map((l) => (
                        <span
                          key={l.key}
                          title={`${l.label}: ${l.green ? "green" : "red"}`}
                          className={`inline-block w-2 h-2 rounded-full ${
                            l.green ? "bg-emerald-500" : "bg-red-500"
                          }`}
                          style={{ border: "1px solid #000" }}
                          aria-hidden
                        />
                      ))}
                      {v.all_green && (
                        <span className="ml-1 text-[0.6rem]" title="All-green">
                          ⭐
                        </span>
                      )}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer positioning line */}
        <div className="text-xs text-muted-foreground max-w-3xl no-print">
          <b>PreOwned Posse</b> is priced as a Phase-2 add-on at{" "}
          <b>$495/month</b> per rooftop on top of the Decline Drive base subscription. Real
          data flows via the DMS sales-master nightly SFTP once integration lands (post Oct 15).
        </div>
      </div>

      {/* CLEARANCE-GATED DEAL DRAWER — click any car to see the numbers */}
      <Dialog
        open={!!activeVehicle}
        onOpenChange={(open) => !open && setActiveVehicle(null)}
      >
        <DialogContent className="max-w-lg" data-testid="posse-deal-drawer">
          {activeVehicle && (() => {
            const t = tierFor(clearance);
            const canCost = tierAllows(t, "cost");
            const canRetail = tierAllows(t, "retail");
            const canGross = tierAllows(t, "gross");
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-mono text-[0.65rem] text-neutral-500">
                      {activeVehicle.stock_no}
                    </span>
                    <span>{activeVehicle.year} {activeVehicle.make_model}</span>
                    {activeVehicle.all_green && (
                      <span className="text-[0.6rem] font-black tracking-widest text-yellow-700 bg-yellow-100 rounded px-1.5 py-0.5">
                        ⭐ ALL-GREEN
                      </span>
                    )}
                  </DialogTitle>
                </DialogHeader>
                <div className="text-[0.65rem] text-neutral-600 font-mono">
                  VIN …{activeVehicle.vin_last6} · Stage:{" "}
                  <span className="capitalize">
                    {(activeVehicle.status || "").replace("_", " ")}
                  </span>{" "}
                  · {activeVehicle.days_in_recon}d in recon
                </div>

                {/* Clearance badge */}
                <div
                  className="rounded-md border p-2 flex items-center gap-2 text-[0.7rem]"
                  style={{ borderColor: BRAND_BRONZE, background: `${BRAND_CREAM}88` }}
                >
                  <span
                    className="font-mono-label text-[0.55rem] tracking-widest"
                    style={{ color: BRAND_BRONZE_DARK }}
                  >
                    CLEARANCE
                  </span>
                  <span className="font-bold">{t.label}</span>
                  <span className="ml-auto text-[0.6rem] italic text-neutral-500">
                    Change in the Cast strip
                  </span>
                </div>

                {/* Status lights row (click to toggle right in the drawer) */}
                {activeVehicle.status_lights && (
                  <div className="flex items-center justify-between gap-2">
                    {activeVehicle.status_lights.map((l) => (
                      <button
                        key={l.key}
                        type="button"
                        onClick={() => toggleLight(activeVehicle.id, l.key, !l.green)}
                        className="flex flex-col items-center gap-0.5 flex-1 hover:scale-110 active:scale-95 transition-transform"
                        data-testid={`posse-drawer-light-${l.key}`}
                      >
                        <span
                          className={`w-4 h-4 rounded-full ${
                            l.green
                              ? "bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.7)]"
                              : "bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.7)] animate-pulse"
                          }`}
                        />
                        <span className="text-[0.55rem] font-mono-label tracking-widest text-neutral-600">
                          {l.label}
                        </span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Money grid — gated by clearance tier */}
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {canCost && (
                    <>
                      <div className="rounded-md border border-border p-2">
                        <div className="font-mono-label text-[0.55rem] tracking-widest text-neutral-500">
                          ACQUIRED COST
                        </div>
                        <div className="font-display font-black text-lg tabular-nums">
                          {money(activeVehicle.acquired_cost)}
                        </div>
                      </div>
                      <div className="rounded-md border border-border p-2">
                        <div className="font-mono-label text-[0.55rem] tracking-widest text-neutral-500">
                          RECON COMMITTED
                        </div>
                        <div className="font-display font-black text-lg tabular-nums text-orange-700">
                          {money(activeVehicle.recon_committed)}
                        </div>
                      </div>
                      <div className="rounded-md border border-border p-2 col-span-2">
                        <div className="font-mono-label text-[0.55rem] tracking-widest text-neutral-500">
                          FRONT-LINE COST
                        </div>
                        <div className="font-display font-black text-xl tabular-nums">
                          {money(activeVehicle.front_line_cost)}
                        </div>
                      </div>
                    </>
                  )}
                  {canRetail && (
                    <div className="rounded-md border border-border p-2 col-span-2">
                      <div className="font-mono-label text-[0.55rem] tracking-widest text-neutral-500">
                        TARGET RETAIL
                      </div>
                      <div className="font-display font-black text-xl tabular-nums">
                        {money(activeVehicle.target_retail)}
                      </div>
                    </div>
                  )}
                  {canGross && (
                    <div
                      className="rounded-md border-2 p-2 col-span-2 bg-emerald-50"
                      style={{ borderColor: "#10b981" }}
                    >
                      <div className="font-mono-label text-[0.55rem] tracking-widest text-emerald-800">
                        {activeVehicle.status === "sold" ? "SOLD GROSS" : "PROJECTED GROSS"}
                      </div>
                      <div className="font-display font-black text-2xl tabular-nums text-emerald-700">
                        {money(activeVehicle.sold_gross ?? activeVehicle.projected_gross)}
                      </div>
                    </div>
                  )}
                  {!canCost && (
                    <div className="col-span-2 rounded-md border-2 border-dashed border-neutral-300 p-4 text-center text-neutral-500 text-[0.75rem]">
                      🔒 Numbers hidden for this clearance level.
                      <br />
                      Ask a manager to open the deal for you.
                    </div>
                  )}
                </div>

                {/* Ownership footer */}
                <div className="rounded-md border border-border p-2 text-[0.7rem] text-neutral-700 space-y-0.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span>
                      Owner:{" "}
                      <b>{activeVehicle.responsible_manager}</b>
                    </span>
                    {/* Deep-link into the SMS thread for this manager */}
                    {(() => {
                      const roster = {
                        "Recon Manager":     "+15551110001",
                        "Body Shop Manager": "+15551110002",
                        "Used Car Manager":  "+15551110003",
                        "Photos / Merch":    "+15551110004",
                        "General Manager":   "+15551110005",
                      };
                      const phone = roster[activeVehicle.responsible_manager];
                      if (!phone) return null;
                      return (
                        <button
                          onClick={() => navigate(`/sms?thread=${encodeURIComponent(phone)}`)}
                          className="ml-auto text-[0.65rem] text-blue-700 hover:text-blue-900 hover:underline font-semibold flex items-center gap-1"
                          data-testid="posse-drawer-see-texts"
                        >
                          <MessageSquareText className="w-3 h-3" strokeWidth={2.4} />
                          See texts
                        </button>
                      );
                    })()}
                  </div>
                  <div>
                    Last touched:{" "}
                    <b>{activeVehicle.last_touched_by || "—"}</b> ·{" "}
                    {activeVehicle.hours_since_touch}h ago
                  </div>
                  {activeVehicle.salesman && (
                    <div>
                      Assigned salesman: <b>{activeVehicle.salesman}</b>
                    </div>
                  )}
                </div>

                {/* Physical intake — keys / tires / glass. Everyone can see
                    and edit this; it's shop data, not money. */}
                {activeVehicle.physical && (
                  <div
                    className="rounded-md border-2 p-3 space-y-3"
                    style={{ borderColor: BRAND_BRONZE, background: `${BRAND_CREAM}55` }}
                    data-testid="posse-drawer-physical"
                  >
                    <div className="flex items-center gap-2">
                      <Wrench className="w-3.5 h-3.5" style={{ color: BRAND_BRONZE_DARK }} strokeWidth={2.4} />
                      <div className="font-mono-label text-[0.6rem] tracking-widest" style={{ color: BRAND_BRONZE_DARK }}>
                        PHYSICAL INTAKE · KEYS · TIRES · GLASS
                      </div>
                    </div>

                    {/* Keys */}
                    <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1 items-center text-[0.75rem]">
                      <Key className="w-4 h-4 text-amber-700" strokeWidth={2.4} />
                      <div className="flex items-center gap-2 flex-wrap">
                        <input
                          type="text"
                          value={activeVehicle.physical.keys?.location || ""}
                          onChange={(e) =>
                            setActiveVehicle((v) => ({
                              ...v,
                              physical: {
                                ...v.physical,
                                keys: { ...v.physical.keys, location: e.target.value },
                              },
                            }))
                          }
                          onBlur={(e) =>
                            api.post(`/posse/vehicles/${activeVehicle.id}/physical`, {
                              section: "keys",
                              patch: { location: e.target.value },
                            })
                          }
                          className="h-7 rounded border border-amber-700/30 bg-white px-2 text-[0.7rem] w-40 font-mono"
                          placeholder="Key box A-12"
                          data-testid="posse-drawer-key-location"
                        />
                        {[
                          { k: "key_1_in_hand", label: "Key 1" },
                          { k: "key_2_in_hand", label: "Key 2" },
                          { k: "fob_working",   label: "Fob" },
                        ].map((f) => {
                          const on = !!activeVehicle.physical.keys?.[f.k];
                          return (
                            <button
                              key={f.k}
                              type="button"
                              onClick={async () => {
                                const next = !on;
                                setActiveVehicle((v) => ({
                                  ...v,
                                  physical: {
                                    ...v.physical,
                                    keys: { ...v.physical.keys, [f.k]: next },
                                  },
                                }));
                                await api.post(
                                  `/posse/vehicles/${activeVehicle.id}/physical`,
                                  { section: "keys", patch: { [f.k]: next } }
                                );
                              }}
                              className={`h-7 px-2 rounded-full border text-[0.65rem] font-semibold ${
                                on
                                  ? "border-emerald-600 bg-emerald-50 text-emerald-800"
                                  : "border-red-400 bg-red-50 text-red-700"
                              }`}
                              data-testid={`posse-drawer-key-${f.k}`}
                            >
                              {on ? "✓" : "✗"} {f.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Tires */}
                    <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1 items-center text-[0.75rem]">
                      <Disc className="w-4 h-4 text-neutral-800" strokeWidth={2.4} />
                      <div className="flex items-center gap-3 flex-wrap">
                        {[
                          { k: "front_32nds", label: "F" },
                          { k: "rear_32nds",  label: "R" },
                        ].map((f) => (
                          <label key={f.k} className="flex items-center gap-1 text-[0.7rem]">
                            <span className="font-mono-label text-[0.55rem] tracking-widest text-neutral-500">
                              {f.label} tread
                            </span>
                            <input
                              type="number"
                              min={0}
                              max={16}
                              value={activeVehicle.physical.tires?.[f.k] ?? ""}
                              onChange={(e) => {
                                const num = Number(e.target.value) || 0;
                                setActiveVehicle((v) => ({
                                  ...v,
                                  physical: {
                                    ...v.physical,
                                    tires: { ...v.physical.tires, [f.k]: num },
                                  },
                                }));
                              }}
                              onBlur={(e) =>
                                api.post(`/posse/vehicles/${activeVehicle.id}/physical`, {
                                  section: "tires",
                                  patch: { [f.k]: Number(e.target.value) || 0 },
                                })
                              }
                              className="w-14 h-7 rounded border border-neutral-300 bg-white px-1 text-[0.7rem] tabular-nums"
                              data-testid={`posse-drawer-tire-${f.k}`}
                            />
                            <span className="text-[0.55rem] text-neutral-500">/32</span>
                          </label>
                        ))}
                        <select
                          value={activeVehicle.physical.tires?.condition || "Pass"}
                          onChange={(e) => {
                            const val = e.target.value;
                            setActiveVehicle((v) => ({
                              ...v,
                              physical: {
                                ...v.physical,
                                tires: { ...v.physical.tires, condition: val },
                              },
                            }));
                            api.post(`/posse/vehicles/${activeVehicle.id}/physical`, {
                              section: "tires",
                              patch: { condition: val },
                            });
                          }}
                          className={`h-7 rounded border px-2 text-[0.65rem] font-semibold ${
                            activeVehicle.physical.tires?.condition === "Pass"
                              ? "border-emerald-600 bg-emerald-50 text-emerald-800"
                              : activeVehicle.physical.tires?.condition === "Replace"
                              ? "border-red-500 bg-red-50 text-red-700"
                              : "border-yellow-500 bg-yellow-50 text-yellow-800"
                          }`}
                          data-testid="posse-drawer-tire-condition"
                        >
                          <option>Pass</option>
                          <option>Mount / rotate</option>
                          <option>Replace</option>
                        </select>
                      </div>
                    </div>

                    {/* Glass */}
                    <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1 items-center text-[0.75rem]">
                      <Square className="w-4 h-4 text-sky-800" strokeWidth={2.4} />
                      <div className="flex items-center gap-2 flex-wrap">
                        <select
                          value={activeVehicle.physical.glass?.windshield || "Pass"}
                          onChange={(e) => {
                            const val = e.target.value;
                            setActiveVehicle((v) => ({
                              ...v,
                              physical: {
                                ...v.physical,
                                glass: { ...v.physical.glass, windshield: val },
                              },
                            }));
                            api.post(`/posse/vehicles/${activeVehicle.id}/physical`, {
                              section: "glass",
                              patch: { windshield: val },
                            });
                          }}
                          className={`h-7 rounded border px-2 text-[0.65rem] font-semibold ${
                            activeVehicle.physical.glass?.windshield === "Pass"
                              ? "border-emerald-600 bg-emerald-50 text-emerald-800"
                              : "border-red-400 bg-red-50 text-red-700"
                          }`}
                          data-testid="posse-drawer-glass-windshield"
                        >
                          <option>Pass</option>
                          <option>Small chip</option>
                          <option>Long crack — order glass</option>
                          <option>Replaced</option>
                        </select>
                        <select
                          value={activeVehicle.physical.glass?.other || "Pass"}
                          onChange={(e) => {
                            const val = e.target.value;
                            setActiveVehicle((v) => ({
                              ...v,
                              physical: {
                                ...v.physical,
                                glass: { ...v.physical.glass, other: val },
                              },
                            }));
                            api.post(`/posse/vehicles/${activeVehicle.id}/physical`, {
                              section: "glass",
                              patch: { other: val },
                            });
                          }}
                          className={`h-7 rounded border px-2 text-[0.65rem] font-semibold ${
                            activeVehicle.physical.glass?.other === "Pass"
                              ? "border-emerald-600 bg-emerald-50 text-emerald-800"
                              : "border-yellow-500 bg-yellow-50 text-yellow-800"
                          }`}
                          data-testid="posse-drawer-glass-other"
                        >
                          <option>Pass</option>
                          <option>Rear defrost line broken</option>
                          <option>Side glass chip</option>
                          <option>Sunroof reseal</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}
